/**
 * Capture current GPS coordinates for app attendance.
 * Uses @capacitor/geolocation on native; falls back to browser Geolocation API.
 */

import { isNative } from "./mobile";

function mapBrowserPosition(position) {
  const { latitude, longitude, accuracy } = position.coords;
  return {
    latitude,
    longitude,
    accuracy: Number.isFinite(accuracy) ? accuracy : undefined,
  };
}

async function getBrowserPosition() {
  if (typeof navigator === "undefined" || !navigator.geolocation) {
    throw new Error("المتصفح لا يدعم تحديد الموقع");
  }

  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      (position) => resolve(mapBrowserPosition(position)),
      (err) => {
        if (err?.code === 1) {
          reject(new Error("يجب السماح بالوصول إلى الموقع لتسجيل الحضور"));
        } else if (err?.code === 2) {
          reject(new Error("تعذر تحديد الموقع — تأكد من تفعيل GPS"));
        } else if (err?.code === 3) {
          reject(new Error("انتهت مهلة تحديد الموقع — حاول مرة أخرى"));
        } else {
          reject(new Error(err?.message || "تعذر تحديد الموقع"));
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 20000,
        maximumAge: 0,
      },
    );
  });
}

async function getNativePosition() {
  const { Geolocation } = await import("@capacitor/geolocation");
  const permission = await Geolocation.checkPermissions();
  let status = permission.location;

  if (status === "prompt" || status === "prompt-with-rationale") {
    const requested = await Geolocation.requestPermissions();
    status = requested.location;
  }

  if (status !== "granted") {
    throw new Error("يجب السماح بالوصول إلى الموقع لتسجيل الحضور");
  }

  const position = await Geolocation.getCurrentPosition({
    enableHighAccuracy: true,
    timeout: 20000,
    maximumAge: 0,
  });

  return {
    latitude: position.coords.latitude,
    longitude: position.coords.longitude,
    accuracy: Number.isFinite(position.coords.accuracy)
      ? position.coords.accuracy
      : undefined,
  };
}

/**
 * @returns {Promise<{ latitude: number, longitude: number, accuracy?: number }>}
 */
export async function getCurrentGpsPosition() {
  try {
    if (isNative()) {
      return await getNativePosition();
    }
    return await getBrowserPosition();
  } catch (err) {
    if (err?.message) throw err;
    throw new Error("تعذر تحديد الموقع");
  }
}

export function formatGpsLabel(latitude, longitude) {
  const lat = Number(latitude).toFixed(6);
  const lng = Number(longitude).toFixed(6);
  return `GPS: ${lat}, ${lng}`;
}
